import { useState } from 'react';
import { Alert, Switch, Table, Typography, message } from 'antd';
import type { TableProps } from 'antd';

import {
  useGetUserFeatureFlagsQuery,
  useToggleUserFeatureFlagMutation,
} from '@/state/slices/featureFlags';
import { UserFeatureFlag } from '@/models/featureFlag';
import styles from './UserFeatureFlags.module.css';

const { Title, Text } = Typography;

export default function UserFeatureFlags() {
  const [messageApi, contextHolder] = message.useMessage();
  const { data: flags, isLoading, isError } = useGetUserFeatureFlagsQuery();
  const [toggleFlag] = useToggleUserFeatureFlagMutation();
  const [savingKey, setSavingKey] = useState<string | null>(null);

  const onToggle = async (flag: UserFeatureFlag, enabled: boolean) => {
    setSavingKey(flag.key);
    try {
      await toggleFlag({ key: flag.key, enabled }).unwrap();
      messageApi.success(
        `${flag.description || flag.key} turned ${enabled ? 'on' : 'off'}`
      );
    } catch {
      messageApi.error(`Failed to update ${flag.description || flag.key}`);
    } finally {
      setSavingKey(null);
    }
  };

  const columns: TableProps<UserFeatureFlag>['columns'] = [
    {
      title: 'Feature',
      key: 'feature',
      render: (_, flag) => (
        <div className={styles.feature}>
          <Text strong>{flag.description || flag.key}</Text>
          <Text type="secondary" code>
            {flag.key}
          </Text>
        </div>
      ),
    },
    {
      title: 'Enabled',
      key: 'enabled',
      width: 120,
      align: 'right',
      render: (_, flag) => (
        <Switch
          aria-label={`Toggle ${flag.description || flag.key}`}
          checked={flag.enabled}
          loading={savingKey === flag.key}
          onChange={(checked) => onToggle(flag, checked)}
        />
      ),
    },
  ];

  return (
    <>
      {contextHolder}
      <Title level={3}>Feature Flags</Title>
      <div className={styles.panel}>
        {isError ? (
          <Alert type="error" showIcon title="Failed to load feature flags" />
        ) : (
          <Table<UserFeatureFlag>
            rowKey="key"
            columns={columns}
            dataSource={flags ?? []}
            loading={isLoading}
            pagination={false}
            locale={{ emptyText: 'No feature flags' }}
          />
        )}
      </div>
    </>
  );
}
